/**
 * Sign in, or start an archive — an email address, a six-character code, a
 * cookie.
 *
 * There is no separate sign-up, and there never was: an address nobody has
 * used before gets an account and an empty archive on the spot. But nothing
 * here said so, so the screen read as a door that only opens for people who
 * already have a key — which is the opposite of what it does, and the thing
 * standing between somebody being told about this and being inside it.
 *
 * There is no password to forget and none to leak. The Worker returns the code
 * in the response body only when it is running on localhost, which is what
 * makes the flow testable without a mail account; in production that field is
 * absent and the code arrives by email.
 */

import { h, replace } from "./dom";
import { icon } from "./icons";
import { api } from "./api";

export function loginScreen(onDone: (created: boolean) => void): HTMLElement {
  const host = h("div", { class: "centre" });
  let email = "";

  const render = (
    step: "email" | "code",
    opts: { error?: string; devCode?: string } = {},
  ): void => {
    const err = opts.error ? h("div", { class: "err", text: opts.error }) : null;

    if (step === "email") {
      const input = h("input", {
        type: "email",
        placeholder: "you@example.com",
        autocomplete: "email",
        value: email,
        required: true,
      });
      const submit = h("button", { class: "btn primary", type: "submit", text: "Continue" });
      const form = h(
        "form",
        {
          onsubmit: (e: Event) => {
            e.preventDefault();
            email = input.value.trim();
            if (!email) return;
            submit.setAttribute("disabled", "");
            submit.textContent = "Sending…";
            void api
              .requestCode(email)
              .then((r) => render("code", { devCode: r.code }))
              .catch((x: unknown) =>
                render("email", {
                  error: x instanceof Error ? x.message : "that did not go through",
                }),
              );
          },
        },
        h("div", { class: "field" }, icon("info"), input),
        submit,
      );
      replace(
        host,
        h(
          "div",
          { class: "login glass" },
          h("div", { class: "brand" }, h("span", { class: "mark" }, icon("album")), "Heirloom"),
          h("p", {
            class: "sub",
            text:
              "The family archive, in a browser. Put in your email address and " +
              "we will send you a code — there is no password to make up or forget.",
          }),
          err,
          form,
          // Said plainly, because the alternative is somebody looking for a
          // sign-up button that does not exist and concluding they need an
          // invitation.
          h("p", {
            class: "sub quiet",
            text:
              "New here? The same address starts a new archive. Nothing is " +
              "created until you enter the code.",
          }),
        ),
      );
      input.focus();
      return;
    }

    const input = h("input", {
      type: "text",
      placeholder: "six characters",
      autocomplete: "one-time-code",
      spellcheck: "false",
      required: true,
    });
    const submit = h("button", { class: "btn primary", type: "submit", text: "Sign in" });
    const form = h(
      "form",
      {
        onsubmit: (e: Event) => {
          e.preventDefault();
          const codeIn = input.value.trim();
          if (!codeIn) return;
          submit.setAttribute("disabled", "");
          submit.textContent = "Checking…";
          void api
            .verify(email, codeIn)
            .then((r) => onDone(r.created))
            .catch((x: unknown) =>
              render("code", {
                error: x instanceof Error ? x.message : "that code was not accepted",
                devCode: opts.devCode,
              }),
            );
        },
      },
      h("div", { class: "field" }, icon("info"), input),
      submit,
      h("button", {
        class: "btn ghost",
        type: "button",
        text: "Use a different address",
        onclick: () => render("email"),
      }),
    );

    replace(
      host,
      h(
        "div",
        { class: "login glass" },
        h("div", { class: "brand" }, h("span", { class: "mark" }, icon("album")), "Heirloom"),
        h("p", { class: "sub", text: `We sent a code to ${email}. It is good for ten minutes.` }),
        opts.devCode
          ? h(
              "div",
              { class: "devcode" },
              "Development server, so the code came back with the response: ",
              h("b", { text: opts.devCode }),
            )
          : null,
        err,
        form,
      ),
    );
    input.focus();
  };

  render("email");
  return host;
}
